import * as React from 'react';
import { IconCash, IconLogoAmericanExpress } from '@tock/icons';
import { OfferingDetailsPrice } from 'components/business/offering/OfferingDetailsPrice';
import { Typography } from 'omakase';
import { consumer } from '@tock/coretypes';
import { isOfferingEventType } from 'utils/offerings';
import ConsumerDisplayMoney from 'components/ConsumerDisplayMoney';
import OfferingDetailsAdditionalCosts from 'components/business/offering/OfferingDetailsAdditionalCosts';
import OfferingDetailsMetadataItem from 'components/business/offering/OfferingDetailsMetadataItem';
import OfferingDetailsType from 'components/business/offering/OfferingDetailsType';

/**
 * Warning!
 * The logic for showing items needs to match the logic for counting items in
 * utils/offerings.ts
 */

interface Props {
  offering: consumer.Offering;
  onNavigateToExperience?(event: consumer.Offering, nextAvailableDate?: string): void;
  businessType?: string;
}

const ExperienceOfferingDetailsSimple: React.FunctionComponent<Props> = ({
  offering,
  onNavigateToExperience,
  businessType,
}) => {
  // Used in Search Modal when clicking on an experience name
  const handleNavigateToExperience = () => {
    if (onNavigateToExperience) {
      onNavigateToExperience(offering);
    }
  };

  if (isOfferingEventType(offering)) {
    return null;
  }

  const { descriptiveVariety, isAmexOnly, largePartyConfig, name, singleUnitQuantity, type } =
    offering;

  const price = offering.price as consumer.OfferedPriceSummary;

  const navigate = !!onNavigateToExperience;

  const experienceTitle = (
    <Typography
      variant="body01Strong"
      component="h2"
      mb={1}
      noWrap
      data-testid="search-experience-name"
    >
      {name}
    </Typography>
  );

  return (
    <div>
      <div className="Consumer-reservationInfo">
        {navigate && (
          <button
            aria-label={`View ${name} (opens a dialog)`}
            onClick={handleNavigateToExperience}
            className="Consumer-reservationLink"
            data-testid="view-detail-button"
          >
            {experienceTitle}
          </button>
        )}
        {!navigate && <>{experienceTitle}</>}
      </div>

      <ul>
        {isAmexOnly && (
          <li className="Consumer-reservationMetaItem">
            <div className="Consumer-reservationMetaItemIcon">
              <IconLogoAmericanExpress />
            </div>
            <Typography component="h2" variant="body02">
              American Express® Card exclusive
            </Typography>
          </li>
        )}
        <OfferingDetailsType offering={offering} businessType={businessType} />
        <OfferingDetailsPrice
          price={price}
          type={type}
          descriptiveVariety={descriptiveVariety}
          singleUnitQuantity={singleUnitQuantity}
        />
        <OfferingDetailsAdditionalCosts price={price} type={type} />

        {largePartyConfig && (
          <OfferingDetailsMetadataItem data-testid="large-party-note" icon={IconCash}>
            Parties of {largePartyConfig.minPartySize}+ require a{' '}
            <ConsumerDisplayMoney
              amount={largePartyConfig.pricePerPersonCents}
              roundIfWholeNumber={true}
            />{' '}
            deposit per person
          </OfferingDetailsMetadataItem>
        )}
      </ul>
    </div>
  );
};

export default ExperienceOfferingDetailsSimple;
